import { Injectable, Type, inject } from "@angular/core";
import { ModalDismissReasons, NgbModal, NgbModalOptions, NgbModalRef } from "@ng-bootstrap/ng-bootstrap";
import { Observable, from } from "rxjs";
import { take } from "rxjs/operators";
import { IContent } from "~app/common/models/validationModel";
import { ObjectUtils } from "~app/common/utils/object-utils";
import { ConfirmModalComponent } from "./common/confirm.component";
import { ErrorModalComponent } from "./common/error.component";
import { InfoModalComponent } from "./common/info.component";
import { IFieldDecleration, PromptDialogComponent, PromptInput, PromptResponse } from "./common/prompt-dialog/prompt-dialog.component";
import { ModalComponentMediator } from "./modal-component-mediator";
import { ModalSettings } from "./modal-models";
import { ModalComponent } from "./modal.component";

@Injectable({
    providedIn: "root"
})
export class RaskModalService {
    private readonly ngbModalService = inject(NgbModal);
    private readonly modalComponentMediator = inject(ModalComponentMediator);

    /**
     * Öpnna en komponent i en modal utan att vara intresserad av ett resultat
     *
     * @param header rubriktext
     * @param innerComponent komponent som ska visas i modal
     * @param size storlek på modalen
     * @param centered om modalen ska vara centrerad eller ej
     * @param data data som skickas med till komponenten
     */
    open<T>(
        header: string,
        innerComponent: Type<T> | string,
        size: RaskModalSize = "sm",
        centered = false,
        data?: any,
        windowClass?: string,
        paramOptions: NgbModalOptions = undefined): ModalComponent {
        const options: NgbModalOptions = ObjectUtils.hasValue(paramOptions) ? paramOptions : {
            size: size,
            centered: centered,
            backdrop: "static",
            windowClass: windowClass
        };
        const obs = this.createModal(innerComponent, options);
        const modal = obs.componentInstance as ModalComponent;
        modal.header = header;
        modal.data = data;
        return modal;
    }

    /**
     * Öpnna en komponent i en modal och vänta in ett resultat
     *
     * @param header rubriktext
     * @param innerComponent komponent som ska visas i modal
     * @param size storlek på modalen
     * @param centered om modalen ska vara centrerad eller ej
     * @param data data som skickas med till komponenten
     * @returns resultatet från komponenten i modalen
     */
    openAsync<T>(header: string, innerComponent: Type<T> | string, size: RaskModalSize = "sm", centered = false, data?: any, options?: NgbModalOptions): Observable<any> {
        const paramOptions: NgbModalOptions = {
            size: size,
            centered: centered,
            backdrop: "static"
        };
        const obs = this.createModal(innerComponent, options ?? paramOptions);
        const modal = obs.componentInstance as ModalComponent;
        modal.header = header;
        modal.data = data;
        return from(obs.result);
    }

    confirm(settings: ModalSettings): Observable<any> {
        const options: NgbModalOptions = {
            size: "lg",
            centered: settings.centered,
            backdrop: "static",
            windowClass: settings.windowClass
        };
        const obs = this.createModal(ConfirmModalComponent, options);
        const modal = obs.componentInstance as ModalComponent;
        modal.header = settings.header;
        modal.getInnerComponent<ConfirmModalComponent>().pipe(
            take(1)
        ).subscribe(c => {
            c.content = settings.text;
            c.messages = settings.messages;
            c.yesCaption = settings.yesCaption;
            c.noCaption = settings.noCaption;
            c.yesDisabled = settings.yesDisabled;
            c.contentIsMarkup = settings.contentIsMarkup;
        });
        return from(obs.result);
    }

    prompt<T extends IFieldDecleration>(header: string, input: PromptInput<T>, size: RaskModalSize = "md"): Observable<PromptResponse<T>> {
        const options: NgbModalOptions = {
            size: size,
            backdrop: "static"
        };
        const obs = this.createModal(PromptDialogComponent, options);
        const modal = obs.componentInstance as ModalComponent;
        modal.header = header;
        modal.getInnerComponent<PromptDialogComponent>().pipe(
            take(1)
        ).subscribe(c => {
            c.input = input;
        });
        return from(obs.result);
    }

    info(settings: ModalSettings): Observable<any> {
        const options: NgbModalOptions = {
            size: "lg",
            backdrop: "static"
        };
        const obs = this.createModal(InfoModalComponent, options);
        const modal = obs.componentInstance as ModalComponent;
        modal.header = settings.header;
        modal.getInnerComponent<InfoModalComponent>().pipe(
            take(1)
        ).subscribe(c => {
            c.content = settings.text;
            c.okCaption = settings.okCaption;
        });
        return from(obs.result);
    }

    errorsWithHandle(header: string, contents: IContent[], backdrop?: boolean | "static"): NgbModalRef {
        const options = {
            backdrop: backdrop,
            windowClass: "errorModal"
        } as NgbModalOptions;
        const modalRef = this.ngbModalService.open(ErrorModalComponent, options);
        const instance = modalRef.componentInstance as ErrorModalComponent;
        instance.header = header;
        instance.messages = contents;
        instance.modalRef = modalRef;
        return modalRef;
    }

    error(header: string, errorContent: IContent, backdrop?: boolean | "static"): Observable<any> {
        const modalRef = this.errorsWithHandle(header, [errorContent], backdrop);
        return from(modalRef.result);
    }

    isClosedByEscape(r: any) {
        return (r === ModalDismissReasons.ESC);
    }

    isClosedByBackdropClick(r: any) {
        return (r === ModalDismissReasons.BACKDROP_CLICK);
    }

    isDialogOpen(): boolean {
        return this.ngbModalService.hasOpenModals();
    }

    private createModal<T>(innerComponent: Type<T> | string, options: NgbModalOptions): NgbModalRef {
        this.modalComponentMediator.setType(innerComponent as Type<T>);
        const modalRef = this.ngbModalService.open(ModalComponent, options);
        this.modalComponentMediator.setRef(modalRef);
        // convert promise to observable
        return modalRef;
    }
}

export type RaskModalSize = "sm" | "md" | "lg" | "xl";
